// gameoptions.ts ↔ game/GameOptions.cpp + game/InputUI.cpp:321-464 — the
// options/menu LOGIC the DOM in-game UI reuses: the game-speed tables +
// GetSpeedMultiplierString, the in-game MENU descriptor (inGameMenuButtons) +
// post-modal handler (handleInGameMenuChoice) + the DoInGameMenu / GAME OPTIONS
// entry points (DoInGameMenu / DoModalGameOptionsForm route to the DOM seams).
// The C++ option globals live on ShellEnv.options (game.cpp:50-62) and the
// gsndm/gpprefs/ggame reaches are the ShellEnv seams.
//
// The canvas option forms (GameOptionsForm / InGameOptionsForm / Sound / Color
// / Display / Performance / DeleteMissionPack) were removed (canvas-removal R1);
// the DOM game-options view drives the read/apply now (dom/ingame/...).

import { kidcCancel } from './uikit.ts';
import {
  newEvent,
  gameOverEvent,
  knGoLoadSavedGame,
  knGoAbortLevel,
  knGoRetryLevel,
  knGoAppStop,
} from './event.ts';
import { kidcHelp, type ShellEnv } from './shellform.ts';
import { kfPlrObserver } from '../../../sim/src/player/side.ts';

// game/res.h — form ids
export const kidfGameOptions = 1004;
export const kidfInGameMenu = 1044;
export const kidfInGameOptions = 1049;
export const kidfSoundOptions = 1050;
export const kidfColorOptions = 1051;
export const kidfDisplayOptions = 1052;
export const kidfPerformanceOptions = 1053;
export const kidfDeleteMissionPack = 1060;

// game/res.h:414-420 — GameOptions form controls
export const kidcInGameOptions = 1001;
export const kidcSoundOptions = 1002;
export const kidcPerformanceOptions = 1003;
export const kidcColorOptions = 1004;
export const kidcDisplayOptions = 1005;
export const kidcDeleteMissionPack = 1007;
export const kidcMiscellaneous = 1008;

// game/res.h:424-434 — InGameOptions controls
export const kidcGameSpeed = 1201;
export const kidcGameSpeedLabel = 1202;
export const kidcLassoSelection = 1003;
export const kidcEasy = 1004;
export const kidcNormal = 1005;
export const kidcHard = 1006;
export const kidcMuteSound = 1007;
export const kidcScrollSpeed = 1204;
export const kidcScrollSpeedLabel = 1205;
export const kidcMouseMode = 1206;

// game/res.h:438-441 — SoundOptions controls
export const kidcVol = 1001;
export const kidcVolLabel = 1002;
export const kidcMute = 1003;
export const kidcVolumeString = 1004;

// game/res.h:445-453 — ColorOptions controls
export const kidcHue = 1002;
export const kidcHueLabel = 1003;
export const kidcSat = 1005;
export const kidcSatLabel = 1006;
export const kidcLum = 1008;
export const kidcLumLabel = 1009;

// game/res.h:457 — DisplayOptions
export const kidcModesList = 1001;

// game/res.h:461-470 — PerformanceOptions
export const kidcRocketShots = 1001;
export const kidcRocketTrails = 1002;
export const kidcRocketImpacts = 1003;
export const kidcShots = 1004;
export const kidcShotImpacts = 1005;
export const kidcSelectionBrackets = 1006;
export const kidcSmoke = 1007;
export const kidcEnemyDamageIndicator = 1008;
export const kidcScorchMarks = 1009;
export const kidcSymbolFlashing = 1010;

// game/res.h:302 — DeleteMissionPack
export const kidcMissionPackList = 1001;

// game/res.h:183-189, 263 — InGameMenu controls
export const kidcSaveGame = 1001;
export const kidcLoadGame = 1002;
export const kidcOptions = 1003;
export const kidcRestartMission = 1004;
export const kidcAbortMission = 1005;
export const kidcObjectives = 1006;
export const kidcExitGame = 1007;
export const kidcChat = 1904;

// ht.h:2737-2738
export const knNotifySelectionChange = 0;
export const knNotifySelectionTap = 1;

// ht.h:5627-5629
export const ksoObjectives = 1;
export const ksoWinSummary = 2;
export const ksoLoseSummary = 3;

// mpshared/netmessage.cpp:10-12 — the C++ table: tGameSpeed = ticks per sim
// update (the timer rate). It caps at t=1 = 8.0x (100 ups, bounded by the 10ms
// tick quantum). THIS ARRAY IS THE WIRE TABLE: creategameform.ts (the MP slider)
// and the MP worker's GAME_SPEEDS/ValidateGameParams both key off it, so it must
// stay byte-for-byte the C++ list — do NOT append to it.
export const gatGameSpeeds = [64, 48, 32, 24, 16, 12, 10, 8, 7, 6, 5, 4, 3, 2, 1];

// BEYOND-SPEC (user-decided extension; see tasks/waves/f11-todo.md DEVIATIONS).
// A 16th SP-ONLY notch at 11.0x. The ideal "ticks per update" is t = 8/11 ≈
// 0.727 — a FRACTIONAL rate the integer timer can't hold (and 0 would spin it),
// so the SP session paces it with an accumulator (gamesession.ts OnTimer SP arm:
// timer stays >= 1 tick, floor(carry) updates per fire). The value 8/11 is the
// sentinel: it is the only sub-1 speed, GetSpeedMultiplierString(8/11) already
// yields "11.0x" (the C++ formula (8*100)/(8/11) = 1100), and it is NEVER written
// to prefs game_speed (always a WASM-safe integer) nor to a GameParams (MP speed
// is the integer wire field, validated server-side against GAME_SPEEDS). SP ONLY.
export const kt11x = 8 / 11;

// The SP slider table: the C++ wire speeds PLUS the 11x notch. Only the SP
// InGameOptions form ranges over this (16 notches). The MP creategameform slider
// keeps ranging over the unmodified gatGameSpeeds (the wire table).
export const gatGameSpeedsSP = [...gatGameSpeeds, kt11x];

/** True for the SP-only fractional 11x notch (the only sub-1 game speed). The
 *  prefs writer and the MP guard test this — t is otherwise an integer rate. */
export const IsFractionalGameSpeed = (t: number): boolean => t > 0 && t < 1;

// GameOptions.cpp:387
export const knScrollSpeedMax = 5;

// game/GameOptions.cpp:137-172 — the web (SDL) build compiles the `#else` arm:
// "iPhone and SDL builds only have InGameOptions". done(fResult) carries the
// C++ return. The in-game GAME OPTIONS renders as a DOM overlay over the live
// canvas (the DOM view reads/applies against THIS env directly, the same seams
// the canvas form used); on close (OK/CANCEL) it calls the passed done.
export function DoModalGameOptionsForm(
  env: ShellEnv,
  _fInGame: boolean,
  done: ((f: boolean) => void) | null = null,
): void {
  if (env.domGameOptions !== undefined) {
    env.domGameOptions(() => {
      if (done !== null) done(true);
    });
    return;
  }
  // No DOM seam wired (e.g. a headless harness) — nothing to show.
  if (done !== null) done(false);
}

//
// InGameOptionsForm implementation
//

// Transplate game update rate into a 'multiplier from normal' string
// game/GameOptions.cpp:350-359
export function GetSpeedMultiplierString(tGameSpeed: number): string {
  let n = 0;
  if (tGameSpeed !== 0) {
    n = Math.trunc((8 * 100) / tGameSpeed);
  }
  const cWhole = Math.trunc(n / 100);
  const cFrac = n % 100;
  return `${cWhole}.${cFrac}x`;
}

//
// The modal in-game MENU (game/InputUI.cpp:321-451) — InputUIForm::InGameMenu's
// body as a free function over ShellEnv (the InputUIDeps.inGameMenu seam target;
// GameSession wires it). done() runs after the menu fully closes.
//
// Presentation is split from logic (HTML/DOM UI shell plan, Phase 5, Form 2):
//  - inGameMenuButtons(env) → the VISIBLE buttons, in order, for the current state
//    (the InputUI.cpp:355-381 Show(false)/SetText/FormatButtons hide+relabel logic).
//  - handleInGameMenuChoice(env, idc, finish) → the post-modal action switch
//    (InputUI.cpp:358-448), driving the DOM path verbatim (it was shared with the
//    canvas path the DOM replaced).
// The DOM path (env.domInGameMenu) feeds
// the SAME handler — the DOM is presentation-only.

// The labels are the forms.ini.pp [kidfInGameMenu] BUTTON texts; the descriptor
// is the SAME visibility/relabel logic InputUI.cpp applies to the canvas controls.
const kszInGameMenu = {
  objectives: 'STATE OBJECTIVES',
  options: 'OPTIONS',
  saveGame: 'SAVE GAME',
  loadGame: 'LOAD GAME',
  restartMission: 'RESTART MISSION',
  abortMission: 'ABORT MISSION',
  chat: 'CHAT',
  help: 'HELP',
  back: 'BACK', // kidcCancel
} as const;

/** A visible in-game-menu button with its EXACT canvas geometry: the control id,
 *  label, and the forms.ini.pp design-stage rect (x,y,w,h in the 160×160 form's
 *  units) the canvas form laid the control at. The DOM view places each button at
 *  this rect verbatim (no view-side auto-stacking), so the DOM is pixel-faithful to
 *  the canvas hide/relabel/FormatButtons result. */
export interface InGameMenuButton {
  idc: number;
  label: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The visible in-game-menu buttons IN ORDER for the current state, each with its
 *  EXACT canvas rect — the canvas Show(false)/SetText/FormatButtons geometry
 *  (InputUI.cpp:331-381 / DoInGameMenu) as a pure descriptor. EXIT WARFARE
 *  (kidcExitGame) is always hidden on the web. Pure — reads only fMultiplayer +
 *  plrLocal.GetFlags(); no DOM.
 *
 *  SP: no FormatButtons runs (the canvas keeps the forms.ini.pp authored rects);
 *  only Chat + Exit Warfare are hidden. The column is the authored y's
 *  (19/36/53/70/87/104), then HELP (30,138,48,14) + BACK (82,138,48,14).
 *
 *  MP: FormatButtons re-stacks the VISIBLE aidcFormat controls
 *  [Objectives, Save(hidden), Load(hidden), Restart(hidden), Abort, Options, Chat,
 *  Exit(hidden), Help(hidden)] at consecutive y's with cySpacing = kidcOptions.top
 *  - kidcObjectives.top = 36-19 = 17 WHILE kidcOptions is still visible — so the
 *  visible run lands at Objectives@19, Abort@36, Options@53, Chat@70. kidcOptions
 *  is hidden AFTER FormatButtons (DoInGameMenu's "Hack" comment), which is exactly
 *  why Chat keeps the y=70 slot with a GAP at y=53. BACK is not in aidcFormat, so
 *  it stays at its authored (82,138). Help is hidden ⇒ the left bottom slot is
 *  empty. */
export function inGameMenuButtons(env: ShellEnv): InGameMenuButton[] {
  if (env.fMultiplayer) {
    const abortLabel =
      (env.plrLocal!.GetFlags() & kfPlrObserver) !== 0 ? 'STOP OBSERVING' : 'RESIGN';
    // the top-row buttons are widened from the authored 100u to 106u (centred on the
    // 160-form at x=27) so the longest label "STATE OBJECTIVES" fits under the wider WI
    // button face (it overshot the 100u box by ~2px); all three stay UNIFORM width.
    return [
      { idc: kidcObjectives, label: kszInGameMenu.objectives, x: 27, y: 19, w: 106, h: 14 },
      { idc: kidcAbortMission, label: abortLabel, x: 27, y: 36, w: 106, h: 14 },
      // GAP at y=53 (where the now-hidden OPTIONS was stacked) — Chat at y=70.
      { idc: kidcChat, label: kszInGameMenu.chat, x: 27, y: 70, w: 106, h: 14 },
      { idc: kidcCancel, label: kszInGameMenu.back, x: 82, y: 138, w: 48, h: 14 },
    ];
  }
  // SP: the forms.ini.pp authored rects (no FormatButtons), Chat + Exit hidden.
  return [
    { idc: kidcObjectives, label: kszInGameMenu.objectives, x: 30, y: 19, w: 100, h: 14 },
    { idc: kidcOptions, label: kszInGameMenu.options, x: 30, y: 36, w: 100, h: 14 },
    { idc: kidcSaveGame, label: kszInGameMenu.saveGame, x: 30, y: 53, w: 100, h: 14 },
    { idc: kidcLoadGame, label: kszInGameMenu.loadGame, x: 30, y: 70, w: 100, h: 14 },
    { idc: kidcRestartMission, label: kszInGameMenu.restartMission, x: 30, y: 87, w: 100, h: 14 },
    { idc: kidcAbortMission, label: kszInGameMenu.abortMission, x: 30, y: 104, w: 100, h: 14 },
    { idc: kidcHelp, label: kszInGameMenu.help, x: 30, y: 138, w: 48, h: 14 },
    { idc: kidcCancel, label: kszInGameMenu.back, x: 82, y: 138, w: 48, h: 14 },
  ];
}

/** The post-modal in-game-menu action switch (InputUI.cpp:358-448) — drives the
 *  DOM path (it was shared with the canvas path the DOM replaced). `finish` is the un-pause epilogue
 *  (un-pause SP + done()); it runs on BACK/kidcCancel and after each action,
 *  exactly like the canvas finish did. Sub-modals (Options/Help/Save/Load/summaries)
 *  hold the pause until they close, then call finish. */
export function handleInGameMenuChoice(env: ShellEnv, idc: number, finish: () => void): void {
  // While the dialog was up the player might have exited the app

  if (!env.evm.IsAppStopping()) {
    // UNDONE: display mode change

    {
      switch (idc) {
        case kidcSaveGame:
          // ggame.SaveGame() — modal (PickSaveGameStream); the C++ blocks
          // through it, so the un-pause waits on the continuation
          if (env.saveGame !== undefined) {
            env.saveGame(() => finish());
            return;
          }
          break;

        case kidcLoadGame: {
          // PickLoadGameStream() — the modal picker (loadsave.ts); the
          // gameOverEvent post is the C++ post-DoModal continuation
          if (env.pickLoadGameStream === undefined) break;
          env.pickLoadGameStream((pstm) => {
            if (pstm !== null) {
              const evt = newEvent(gameOverEvent);
              evt.dw = knGoLoadSavedGame;
              env.setSavedGameStream?.(pstm); // gpstmSavedGame = pstm
              env.evm.PostEvent(evt);
            }
            finish();
          });
          return;
        }

        case kidcAbortMission:
        case kidcRestartMission: {
          // InputUI.cpp:391-407 — the modal summary runs to completion (the
          // C++ blocks in ShowObjectives), THEN the gameOver/observe arm
          const afterObjectives = (): void => {
            // if (!gfMultiplayer || observer || !ggame.AskObserveGame())
            //   post gameOverEvent (InputUI.cpp:408-420)
            const conclude = (fObserve: boolean): void => {
              if (!fObserve) {
                const evt = newEvent(gameOverEvent);
                evt.dw = idc === kidcAbortMission ? knGoAbortLevel : knGoRetryLevel;
                env.evm.PostEvent(evt);
              }
              finish();
            };
            if (
              !env.fMultiplayer ||
              (env.plrLocal !== null && (env.plrLocal.GetFlags() & kfPlrObserver) !== 0) ||
              env.askObserveGame === undefined
            ) {
              conclude(false);
              return;
            }
            env.askObserveGame(conclude); // ggame.AskObserveGame()
          };
          const proceed = (): void => {
            if (
              (env.plrLocal === null || (env.plrLocal.GetFlags() & kfPlrObserver) === 0) &&
              env.showObjectives !== undefined
            ) {
              // HACK: only ksoWinSummary has the fields necessary to
              // support the fAborting flag
              env.showObjectives(
                env.fMultiplayer ? ksoLoseSummary : ksoWinSummary,
                false,
                env.fMultiplayer ? false : true,
                () => afterObjectives(),
              );
              return;
            }
            afterObjectives();
          };
          if (env.fMultiplayer) {
            // if (!ggame.AskResignGame()) break; (InputUI.cpp:386-388)
            if (env.askResignGame === undefined) break;
            env.askResignGame((fYes) => {
              if (!fYes) {
                finish(); // the C++ break (the un-pause epilogue)
                return;
              }
              proceed();
            });
            return;
          }
          proceed();
          return;
        }

        case kidcOptions:
          DoModalGameOptionsForm(env, true, () => finish());
          return;

        case kidcHelp:
          // the FULL manual as a DOM overlay (the same renderer as the main-menu
          // HELP); finish is the un-pause epilogue. (Builder/build-panel "?" routes
          // the SECTION-only env.domHelp at the BuildUI layer — distinct from this.)
          if (env.domHelpManual !== undefined) {
            env.domHelpManual(() => finish());
            return;
          }
          // No DOM help seam wired (e.g. a headless harness) — nothing to show.
          finish();
          return;

        case kidcExitGame: {
          const proceed = (): void => {
            const evt = newEvent(gameOverEvent);
            evt.dw = knGoAppStop;
            env.evm.PostEvent(evt);

            env.saveReinitializeGame?.(); // ggame.SaveReinitializeGame()
            finish();
          };
          if (env.fMultiplayer) {
            // if (!ggame.AskResignGame()) break;
            if (env.askResignGame === undefined) break;
            env.askResignGame((fYes) => {
              if (!fYes) {
                finish(); // the C++ break (the un-pause epilogue)
                return;
              }
              proceed();
            });
            return;
          }
          proceed();
          return;
        }

        case kidcObjectives:
          // gpplrLocal->ShowObjectives(ksoObjectives) — modal; the C++ blocks
          // through it before the un-pause
          if (env.showObjectives !== undefined) {
            env.showObjectives(ksoObjectives, undefined, undefined, () => finish());
            return;
          }
          break;

        case kidcChat:
          env.showChat?.(); // m_pchatter->ShowChat() (MP, M5)
          break;
      }
    }
  }

  finish();
}

export function DoInGameMenu(env: ShellEnv, done: (() => void) | null = null): void {
  // The in-game PAUSE MENU renders as a DOM overlay over the live canvas. Hold
  // the SP pause (MP can't pause) around the overlay; route the chosen idc
  // through the SAME post-modal handler. The descriptor (visible buttons +
  // relabels) is the pure inGameMenuButtons; no canvas form is built.
  if (env.domInGameMenu !== undefined) {
    if (!env.fMultiplayer) env.sim?.Pause(true);
    const finish = (): void => {
      if (!env.fMultiplayer) env.sim?.Pause(false);
      if (done !== null) done();
    };
    env.domInGameMenu(inGameMenuButtons(env), (idc) => handleInGameMenuChoice(env, idc, finish));
    return;
  }

  // No DOM pause-menu seam wired (e.g. a headless harness) — nothing to show.
  if (done !== null) done();
}
